import React, { useMemo, useState } from "react";
import { Alert, Box, Container, CopyToClipboard, Header, SpaceBetween, Table } from "@cloudscape-design/components";
import { FailedExecution, FailedExecutionsResult } from "../../services/StepFunctionFailuresService";
import { SearchedContext } from "../../hooks/useStepFunctionFailures";
import { ResultFormatter } from "../../utils";
import { SFN_FAILURES_LABELS } from "../../constants";
import { buildFailedExecutionColumnDefinitions } from "./tableDefinitions";
import { FailureDetailModal } from "./FailureDetailModal";

interface StepFunctionFailuresResultsProps {
  result: FailedExecutionsResult;
  searchedContext: SearchedContext;
}

export const StepFunctionFailuresResults: React.FC<StepFunctionFailuresResultsProps> = ({ result, searchedContext }) => {
  const [selectedExecution, setSelectedExecution] = useState<FailedExecution | null>(null);

  const columnDefinitions = useMemo(() => buildFailedExecutionColumnDefinitions(setSelectedExecution), []);

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${result.executions.length})`}
            actions={
              result.executions.length > 0 ? (
                <CopyToClipboard
                  copyButtonText={SFN_FAILURES_LABELS.COPY_ALL}
                  copySuccessText={SFN_FAILURES_LABELS.COPIED}
                  copyErrorText={SFN_FAILURES_LABELS.COPY_FAILED}
                  textToCopy={ResultFormatter.formatFailedExecutions(
                    searchedContext.stateMachineName,
                    searchedContext.realm,
                    result.executions
                  )}
                />
              ) : (
                undefined
              )
            }
          >
            {SFN_FAILURES_LABELS.RESULTS}
          </Header>
        }
      >
        <SpaceBetween size="m">
          {result.truncated && (
            <Alert type="warning" header={SFN_FAILURES_LABELS.TRUNCATED_WARNING_HEADER}>
              {SFN_FAILURES_LABELS.TRUNCATED_WARNING}
            </Alert>
          )}
          <Table
            columnDefinitions={columnDefinitions}
            items={result.executions}
            variant="embedded"
            resizableColumns
            stripedRows
            empty={
              <Box textAlign="center" color="text-body-secondary">
                {SFN_FAILURES_LABELS.NO_FAILURES}
              </Box>
            }
          />
        </SpaceBetween>
      </Container>

      <FailureDetailModal execution={selectedExecution} visible={!!selectedExecution} onDismiss={() => setSelectedExecution(null)} />
    </>
  );
};
